import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, KeyRound } from "lucide-react";
import { ApiError, getMe } from "@/lib/api";
import {
  apexDomain,
  goToAdminHost,
  goToClubHost,
  hostKind,
  stashSocialOrigin,
  takeSocialOriginAdmin,
  takeSocialOriginClub,
} from "@/lib/hostKind";
import { unscopedKey } from "@/lib/queryClient";
import { Button } from "@/components/ui/Button";
import {
  firstFlowError,
  kratosErrorCopy,
  nodeMessages,
  oidcLinkLoginRequest,
  oidcProviders,
  submitOidcProvider,
  type KratosOidcLinkLoginRequest,
} from "@/lib/kratos";
import {
  exchangeKratosSession,
  linkKratosSocialSession,
} from "@/features/auth/kratos/exchange";
import { useKratosFlow } from "@/features/auth/kratos/useKratosFlow";
import { KratosStepUpPanel } from "@/features/auth/kratos/KratosStepUpPanel";
import { AuthTransitionScreen } from "@/features/auth/components/AuthTransitionScreen";
import {
  conditionalUiAvailable,
  flowSupportsPasskeyLogin,
  passkeyLogin,
  webauthnSupported,
} from "@/features/auth/kratos/webauthn";

type SocialAccountLinkRequest = {
  email: string;
  provider: string;
  providerLabel: string;
};

/**
 * Kratos-mode sign-in: email + password form, passkey button, and
 * conditional-UI passkey autofill — all rendered by the SPA, zero
 * redirects. A completed Kratos ceremony is mirrored into the Tktic
 * app session via `POST /api/auth/session/exchange`.
 */
export function KratosLoginPanel({
  nextPath,
  flowId,
  autoExchange = false,
  socialStartProvider = null,
  selfServiceEnabled = true,
  socialLoginEnabled = false,
  googleEnabled = true,
  appleEnabled = true,
}: {
  nextPath: string;
  flowId?: string;
  autoExchange?: boolean;
  /** When set (on the APEX host), auto-start this OIDC provider's flow as
   * soon as the login flow is ready — the ceremony must run entirely on the
   * apex so Kratos's host-scoped `ory_kratos_continuity` cookie is readable
   * on the fixed apex callback. Set by a club-host social click redirecting
   * to `<apex>/login?provider=…&social-start=1`. See handleSocialClick. */
  socialStartProvider?: string | null;
  selfServiceEnabled?: boolean;
  socialLoginEnabled?: boolean;
  googleEnabled?: boolean;
  appleEnabled?: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [exchangeError, setExchangeError] = useState<string | null>(null);
  const [socialError, setSocialError] = useState<string | null>(null);
  const [socialBusy, setSocialBusy] = useState<string | null>(null);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [linkRequest, setLinkRequest] = useState<SocialAccountLinkRequest | null>(null);
  const [linkPassword, setLinkPassword] = useState("");
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [relinkPassword, setRelinkPassword] = useState("");
  const [relinkError, setRelinkError] = useState<string | null>(null);
  // MFA-at-login gate: the exchange 403'd with `mfa_login_required` — the
  // Kratos ceremony succeeded at aal1 but this user requires a second factor
  // before an app session is minted. Render the aal2 step-up inline.
  const [mfaChallenge, setMfaChallenge] = useState(false);
  const socialReturnTo = useMemo(
    () => socialAuthReturnTo("/login", nextPath),
    [nextPath],
  );

  // True for a destination that only exists on the OPERATOR host — a club host
  // has no such route (SuperAdminShell 404s when a host club is present). Used
  // as a belt-and-suspenders signal alongside the stashed admin-origin marker.
  const isOperatorPath = (path: string | null | undefined): boolean =>
    typeof path === "string" &&
    (path === "/super-admin" || path.startsWith("/super-admin/"));

  // Which club host to send an apex social-login landing to: the club the
  // flow STARTED on wins (stashed cookie); otherwise, if the freshly-exchanged
  // session belongs to exactly one club, use that. Returns null to stay put.
  const resolveClubToReturnTo = async (): Promise<string | null> => {
    const origin = takeSocialOriginClub();
    if (origin) return origin;
    try {
      const me = await getMe();
      const memberships = me.club_memberships ?? [];
      if (memberships.length === 1) return memberships[0].club_slug;
    } catch {
      // /api/me failed — fall through to normal (apex) routing.
    }
    return null;
  };

  // Post-exchange routing only — no exchange, no cache invalidation. Split
  // out of finishSignIn so the inline MFA step-up (which performs its OWN
  // exchange + invalidations) can hand off here via `onCompleted` without
  // exchanging twice.
  const completeSignInNavigation = async () => {
    // Multi-club social-login safety net. OIDC returns to the FIXED apex
    // callback and Kratos doesn't reliably carry the club `return_to` back
    // through it, so a user who started on `<club>.<apex>` can land here on
    // the bare apex — where club routes 404. When we're on the apex, send
    // the browser to the right club host: prefer the club they STARTED on
    // (stashed before the provider redirect), else fall back to their
    // membership (exactly one club → go there; zero/many → stay on the apex
    // and let normal routing handle it). Only reachable in real multi-club
    // hosting; on a club host `hostKind()` is "club" and we skip straight to
    // the normal navigate. See docs/KRATOS.md.
    if (hostKind() === "apex") {
      // Operator origin wins over any club home: a super-admin who started
      // the flow on `admin.<apex>` (or is heading to an operator-only path)
      // must return to the admin host — a club host has no `/super-admin`
      // route and would render 404. Checked BEFORE the club-membership
      // fallback below, which would otherwise send an operator who also
      // happens to hold a single club membership to that club.
      const adminOrigin = takeSocialOriginAdmin();
      if (adminOrigin || isOperatorPath(nextPath)) {
        goToAdminHost(isOperatorPath(nextPath) ? nextPath : "/super-admin");
        return;
      }
      const target = await resolveClubToReturnTo();
      if (target) {
        goToClubHost(target, nextPath);
        return;
      }
    }
    void navigate({ to: nextPath, replace: true });
  };

  const finishSignIn = async () => {
    setExchangeError(null);
    try {
      await exchangeKratosSession();
      await queryClient.invalidateQueries();
      await queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
      await completeSignInNavigation();
    } catch (err) {
      const pendingLink = accountLinkRequestFromError(err);
      if (pendingLink) {
        setLinkRequest(pendingLink);
        setLinkPassword("");
        setLinkError(null);
        setSocialError(null);
        return;
      }
      if (isMfaLoginRequired(err)) {
        setMfaChallenge(true);
        return;
      }
      setExchangeError(exchangeErrorCopy(err));
    }
  };

  const { flow, loading, submitting, error, submit } = useKratosFlow("login", {
    flowId,
    createParams: { return_to: socialReturnTo },
    onSuccess: finishSignIn,
    // On an OIDC `?social=1` return we go straight to exchange; creating a
    // login flow here would 400 `session_already_available` and flash that
    // error before the exchange redirects.
    skip: autoExchange,
  });
  // Latest submit in a ref so the one-shot conditional-UI ceremony
  // below doesn't restart every time the flow object changes identity.
  const submitRef = useRef(submit);
  submitRef.current = submit;

  // Kratos OIDC re-link login flow: the verified social email already
  // belongs to a Kratos identity (e.g. the user unlinked Google then signed
  // in with Google again), so Kratos asks them to authenticate with their
  // existing password to re-attach the provider. The flow already carries
  // the identifier; submit it with the confirmed password through the same
  // login flow, then `finishSignIn` exchanges the resulting session.
  const relinkRequest = useMemo<KratosOidcLinkLoginRequest | null>(
    () => (flow ? oidcLinkLoginRequest(flow) : null),
    [flow],
  );

  const autoExchangeStarted = useRef(false);
  useEffect(() => {
    if (!autoExchange || autoExchangeStarted.current) return;
    autoExchangeStarted.current = true;
    void finishSignIn();
    // One-shot resume after a provider redirects back to /login.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoExchange]);

  // Conditional-UI autofill: arm one pending assertion per flow; when
  // the user picks a passkey from the email input's autofill UI, the
  // promise resolves and we auto-submit.
  const passkeyAvailable =
    !linkRequest && !relinkRequest && !!flow && flowSupportsPasskeyLogin(flow);
  const flowForAutofill = passkeyAvailable ? flow : null;
  useEffect(() => {
    if (!flowForAutofill) return;
    const controller = new AbortController();
    let cancelled = false;
    void (async () => {
      if (!(await conditionalUiAvailable())) return;
      try {
        const assertion = await passkeyLogin(flowForAutofill, {
          conditional: true,
          signal: controller.signal,
        });
        if (!cancelled) {
          await submitRef.current({ method: "passkey", passkey_login: assertion });
        }
      } catch {
        // Aborted (unmount/re-arm) or dismissed — silent by design.
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // Re-arm only when the underlying flow (and its challenge) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowForAutofill?.id]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !flow) return;
    setExchangeError(null);
    setLinkRequest(null);
    setLinkPassword("");
    setLinkError(null);
    await submit({ method: "password", identifier: email.trim(), password });
  };

  const handleRelinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !flow || !relinkRequest) return;
    setRelinkError(null);
    try {
      await submit({
        method: "password",
        identifier: relinkRequest.email,
        password: relinkPassword,
      });
    } catch (err) {
      setRelinkError(kratosErrorCopy(err));
    }
  };

  // The re-link prompt is derived from the resumed Kratos flow, so it can't
  // be dismissed by clearing local state (unlike the social-link confirm).
  // Drop the `?flow=` deep link and land on a clean /login, which starts a
  // fresh login flow with the normal email/password + passkey options.
  const handleRelinkCancel = () => {
    setRelinkPassword("");
    setRelinkError(null);
    void navigate({ to: "/login", replace: true });
  };

  const handleLinkCancel = () => {
    setLinkRequest(null);
    setLinkPassword("");
    setLinkError(null);
    setSocialBusy(null);
    // On an OIDC `?social=1` return the auto-exchange is one-shot, so simply
    // clearing linkRequest would re-engage the loading guard with no way to
    // re-run it. Drop `?social=1` and land on a clean /login (the normal
    // email/password + passkey form) instead of trapping the spinner.
    if (autoExchange) {
      void navigate({ to: "/login", replace: true });
    }
  };

  const handlePasskeyClick = async () => {
    if (!flow || passkeyBusy) return;
    setPasskeyBusy(true);
    setExchangeError(null);
    setLinkRequest(null);
    setLinkPassword("");
    setLinkError(null);
    try {
      const assertion = await passkeyLogin(flow);
      await submit({ method: "passkey", passkey_login: assertion });
    } catch {
      // User dismissed the platform dialog — nothing to surface.
    } finally {
      setPasskeyBusy(false);
    }
  };

  const handleSocialClick = async (providerId: string) => {
    if (socialBusy) return;
    // The ENTIRE Kratos OIDC ceremony must run on the apex. Kratos scopes its
    // `ory_kratos_continuity` cookie (which carries the OIDC PKCE/auth-code
    // state) to the host that CREATED the flow; the provider callback is the
    // fixed apex host, so a flow created on a club subdomain leaves an
    // unreadable continuity cookie there and Kratos 400s the callback with
    // "no resumable session found" (cross-subdomain OIDC continuity is
    // Ory-Enterprise-only). So on a CLUB host we don't submit here — we stash
    // the origin club (survives on the shared `.<apex>` cookie) and hand off
    // to `<apex>/login?provider=…&social-start=1`, where this panel re-mounts
    // and auto-starts the flow same-origin. finishSignIn then returns the user
    // to their club. See LoginPage + docs/KRATOS.md.
    if (hostKind() !== "apex") {
      // Remember where the flow started so the apex landing can return the user
      // there: a CLUB host stashes its slug, the OPERATOR host sets the admin
      // marker (it has no slug — otherwise a super-admin is routed to a club
      // home where /super-admin 404s). `stashSocialOrigin` picks by host.
      stashSocialOrigin();
      const apexLogin = new URL("/login", `https://${apexDomain()}`);
      apexLogin.searchParams.set("provider", providerId);
      apexLogin.searchParams.set("social-start", "1");
      if (nextPath && nextPath !== "/hub") {
        apexLogin.searchParams.set("next", nextPath);
      }
      setSocialBusy(providerId);
      window.location.assign(apexLogin.toString());
      return;
    }
    // On the apex: run the ceremony here (same-origin as the callback).
    if (!flow) return;
    setSocialBusy(providerId);
    setSocialError(null);
    setExchangeError(null);
    setLinkRequest(null);
    setLinkPassword("");
    setLinkError(null);
    try {
      const outcome = await submitOidcProvider("login", flow, providerId);
      if (outcome.kind === "success") {
        await finishSignIn();
      } else if (outcome.kind === "flow") {
        setSocialError(firstFlowError(outcome.flow) ?? "Social sign-in could not start.");
        setSocialBusy(null);
      }
    } catch (err) {
      setSocialError(kratosErrorCopy(err));
      setSocialBusy(null);
    }
  };

  // Apex auto-start: a club-host social click hands off to
  // `<apex>/login?provider=…&social-start=1`; once the login flow is ready
  // here on the apex, submit that provider so the whole ceremony (incl. the
  // continuity cookie) is same-origin with the callback. One-shot per mount.
  const socialStartRef = useRef(false);
  useEffect(() => {
    if (!socialStartProvider || socialStartRef.current) return;
    if (!flow || socialBusy) return;
    socialStartRef.current = true;
    void handleSocialClick(socialStartProvider);
    // Only re-evaluate as the flow becomes available; handleSocialClick is
    // stable enough for this one-shot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socialStartProvider, flow]);

  const handleLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkRequest || linking) return;
    setLinking(true);
    setLinkError(null);
    setExchangeError(null);
    try {
      await linkKratosSocialSession(linkPassword);
      await queryClient.invalidateQueries();
      await queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
      void navigate({ to: nextPath, replace: true });
    } catch (err) {
      setLinkError(accountLinkErrorCopy(err));
    } finally {
      setLinking(false);
    }
  };

  const flowError = flow ? firstFlowError(flow) : null;
  const identifierMessages = flow ? nodeMessages(flow, "identifier") : [];
  const passwordMessages = flow ? nodeMessages(flow, "password") : [];
  const socialProviders = flow
    ? oidcProviders(flow).filter((provider) =>
        socialProviderAllowed(provider.id, {
          socialLoginEnabled,
          googleEnabled,
          appleEnabled,
        }) && !provider.disabled,
      )
    : [];

  // Full-screen "Signing you in…" spinner across the two OIDC redirect legs, so
  // the login form never flashes between hops:
  //   - OUTBOUND (`?social-start=1`): `socialStartProvider` is set and the
  //     one-shot effect below auto-submits the provider once the flow loads —
  //     a guaranteed full-page redirect to Google/Apple. The panel MUST stay
  //     mounted for that effect to run, so the spinner lives here (not a
  //     LoginPage early-return, which would prevent the ceremony from starting).
  //   - RETURN (`?social=1` / `?id=` → auto-exchange): stay on the spinner
  //     until the exchange redirects, bailing out only when there's something
  //     to show (a hard error, or a pending account-link / re-link prompt).
  const inSocialStart = !!socialStartProvider && !socialError && !mfaChallenge;
  const inAutoExchange =
    autoExchange && !exchangeError && !linkRequest && !relinkRequest && !mfaChallenge;
  if (inSocialStart || inAutoExchange) {
    return (
      <div data-testid="login-kratos-loading">
        <AuthTransitionScreen />
      </div>
    );
  }
  if (mfaChallenge) {
    // Password (or recovery) ceremony done, but this account requires a
    // second factor at login. The step-up panel runs the aal2 flow against
    // the existing first-factor Kratos session, re-exchanges itself, then
    // hands routing back to completeSignInNavigation.
    return (
      <div className="flex flex-col gap-4" data-testid="login-mfa-challenge">
        <KratosStepUpPanel
          variant="login"
          nextPath={nextPath}
          allowRememberDevice
          onCompleted={completeSignInNavigation}
          onCancel={() => {
            setMfaChallenge(false);
            // On an auto-exchange (OIDC/registration return) the one-shot
            // effect has already fired — clearing the challenge would trap
            // the user on the transition spinner. Land on a clean /login
            // instead (same escape as handleLinkCancel).
            if (autoExchange) {
              void navigate({ to: "/login", replace: true });
            }
          }}
        />
      </div>
    );
  }
  if (loading) {
    return (
      <p data-testid="login-kratos-loading" className="font-mono text-xs text-ink-muted">
        Preparing sign-in...
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="login-kratos-panel">
      {relinkRequest ? (
        <OidcRelinkConfirm
          request={relinkRequest}
          password={relinkPassword}
          error={relinkError ?? firstFlowError(flow!) ?? error}
          busy={submitting}
          onPasswordChange={setRelinkPassword}
          onSubmit={handleRelinkSubmit}
          onUseAnotherMethod={handleRelinkCancel}
        />
      ) : linkRequest ? (
        <SocialAccountLinkConfirm
          request={linkRequest}
          password={linkPassword}
          error={linkError}
          busy={linking}
          onPasswordChange={setLinkPassword}
          onSubmit={handleLinkSubmit}
          onCancel={handleLinkCancel}
        />
      ) : (
        <form
          onSubmit={handlePasswordSubmit}
          data-testid="login-kratos-form"
          className="flex flex-col gap-4"
        >
        {socialProviders.length ? (
          <div data-testid="login-social-providers" className="grid gap-2">
            {socialProviders.map((provider) => (
              <Button
                key={provider.id}
                type="button"
                variant="outline"
                onClick={() => void handleSocialClick(provider.id)}
                disabled={!!socialBusy || submitting}
                data-testid={`login-social-${provider.id.toLowerCase()}`}
                className="w-full min-w-0"
              >
                <SocialProviderMark providerId={provider.id} />
                <span className="truncate">
                  {socialBusy === provider.id
                    ? "Opening..."
                    : `Continue with ${provider.label}`}
                </span>
              </Button>
            ))}
          </div>
        ) : null}
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Email
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            // `webauthn` in autocomplete opts this input into the
            // passkey conditional-UI (autofill) flow armed above.
            autoComplete="username webauthn"
            autoFocus
            required
            data-testid="login-kratos-email"
            className="rounded border border-rule bg-canvas px-3 py-2.5 text-[16px] text-ink focus:outline-none focus:ring-2 focus:ring-accent sm:text-sm"
          />
          {identifierMessages.map((message) => (
            <span key={message} className="font-mono text-[0.65rem] text-destructive">
              {message}
            </span>
          ))}
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Password
          </span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            data-testid="login-kratos-password"
            className="rounded border border-rule bg-canvas px-3 py-2.5 text-[16px] text-ink focus:outline-none focus:ring-2 focus:ring-accent sm:text-sm"
          />
          {passwordMessages.map((message) => (
            <span key={message} className="font-mono text-[0.65rem] text-destructive">
              {message}
            </span>
          ))}
        </label>

        {flowError ? (
          <p data-testid="login-kratos-error" className="font-mono text-xs text-destructive">
            {flowError}
          </p>
        ) : null}
        {error ? (
          <p data-testid="login-kratos-client-error" className="font-mono text-xs text-destructive">
            {error}
          </p>
        ) : null}
        {socialError ? (
          <p data-testid="login-social-error" className="font-mono text-xs text-destructive">
            {socialError}
          </p>
        ) : null}
        {exchangeError ? (
          <div
            data-testid="login-kratos-exchange-error"
            className="flex flex-col gap-1 font-mono text-xs text-destructive"
          >
            <span>{exchangeError}</span>
            {exchangeError.includes("verify") ? (
              <Link
                to="/verify-email"
                data-testid="login-kratos-verify-link"
                className="self-start text-accent hover:underline"
              >
                Verify your email
              </Link>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
          {passkeyAvailable && webauthnSupported() ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => void handlePasskeyClick()}
              disabled={passkeyBusy || submitting}
              data-testid="login-kratos-passkey"
              className="w-full sm:w-auto"
            >
              <KeyRound aria-hidden="true" />
              {passkeyBusy ? "Waiting for passkey..." : "Sign in with a passkey"}
            </Button>
          ) : null}
          <Button
            type="submit"
            disabled={submitting}
            data-testid="login-kratos-submit"
            className="w-full sm:w-auto"
          >
            {submitting ? (
              "Signing in..."
            ) : (
              <>
                Sign in
                <ArrowRight aria-hidden="true" />
              </>
            )}
          </Button>
        </div>
      </form>
      )}

      <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-rule pt-3 text-[0.65rem] font-mono text-ink-muted">
        <Link
          to="/recovery"
          data-testid="login-kratos-recovery"
          className="rounded px-1 py-0.5 hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          Forgot password?
        </Link>
        {selfServiceEnabled ? (
          <Link
            to="/register"
            data-testid="login-register"
            className="rounded px-1 py-0.5 hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            Create account
          </Link>
        ) : null}
      </footer>
    </div>
  );
}

function SocialAccountLinkConfirm({
  request,
  password,
  error,
  busy,
  onPasswordChange,
  onSubmit,
  onCancel,
}: {
  request: SocialAccountLinkRequest;
  password: string;
  error: string | null;
  busy: boolean;
  onPasswordChange: (value: string) => void;
  onSubmit: (event: React.FormEvent) => void;
  onCancel: () => void;
}) {
  return (
    <form
      onSubmit={onSubmit}
      data-testid="login-social-link-panel"
      className="flex flex-col gap-4 rounded border border-rule bg-surface p-3"
    >
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-ink">
          This email already has a Tktic account.
        </p>
        <p className="text-xs leading-5 text-ink-muted">
          Confirm your password to link {request.providerLabel} for {request.email}.
        </p>
      </div>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Account password
        </span>
        <input
          type="password"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          autoComplete="current-password"
          autoFocus
          required
          data-testid="login-social-link-password"
          className="rounded border border-rule bg-canvas px-3 py-2.5 text-[16px] text-ink focus:outline-none focus:ring-2 focus:ring-accent sm:text-sm"
        />
      </label>

      {error ? (
        <p data-testid="login-social-link-error" className="font-mono text-xs text-destructive">
          {error}
        </p>
      ) : null}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={busy}
          data-testid="login-social-link-cancel"
          className="w-full sm:w-auto"
        >
          Use another method
        </Button>
        <Button
          type="submit"
          disabled={busy}
          data-testid="login-social-link-submit"
          className="w-full sm:w-auto"
        >
          {busy ? (
            "Linking..."
          ) : (
            <>
              Link {request.providerLabel}
              <ArrowRight aria-hidden="true" />
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

function OidcRelinkConfirm({
  request,
  password,
  error,
  busy,
  onPasswordChange,
  onSubmit,
  onUseAnotherMethod,
}: {
  request: KratosOidcLinkLoginRequest;
  password: string;
  error: string | null;
  busy: boolean;
  onPasswordChange: (value: string) => void;
  onSubmit: (event: React.FormEvent) => void;
  onUseAnotherMethod: () => void;
}) {
  return (
    <form
      onSubmit={onSubmit}
      data-testid="login-oidc-relink-panel"
      className="flex flex-col gap-4 rounded border border-rule bg-surface p-3"
    >
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-ink">
          Confirm your password to reconnect {request.providerLabel}.
        </p>
        <p className="text-xs leading-5 text-ink-muted">
          {request.email} already has a Tktic account. Sign in with your
          password to add {request.providerLabel} back as a sign-in method.
        </p>
      </div>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Email
        </span>
        <input
          type="email"
          value={request.email}
          readOnly
          data-testid="login-oidc-relink-email"
          className="rounded border border-rule bg-elevated px-3 py-2.5 text-[16px] text-ink-muted sm:text-sm"
        />
      </label>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Account password
        </span>
        <input
          type="password"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          autoComplete="current-password"
          autoFocus
          required
          data-testid="login-oidc-relink-password"
          className="rounded border border-rule bg-canvas px-3 py-2.5 text-[16px] text-ink focus:outline-none focus:ring-2 focus:ring-accent sm:text-sm"
        />
      </label>

      {error ? (
        <p data-testid="login-oidc-relink-error" className="font-mono text-xs text-destructive">
          {error}
        </p>
      ) : null}

      {/* Stacked, full-width: the "Use password or passkey instead" label is
          too long to sit reliably beside the submit button without breaching
          the panel border on narrow widths. */}
      <div className="flex flex-col gap-2">
        <Button
          type="submit"
          disabled={busy}
          data-testid="login-oidc-relink-submit"
          className="w-full min-w-0"
        >
          {busy ? (
            "Reconnecting..."
          ) : (
            <>
              <span className="truncate">Reconnect {request.providerLabel}</span>
              <ArrowRight aria-hidden="true" />
            </>
          )}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onUseAnotherMethod}
          disabled={busy}
          data-testid="login-oidc-relink-cancel"
          className="w-full min-w-0"
        >
          <span className="truncate">Use password or passkey instead</span>
        </Button>
      </div>
    </form>
  );
}

function socialAuthReturnTo(pathname: "/login" | "/register", nextPath?: string): string {
  const url = new URL(pathname, window.location.origin);
  url.searchParams.set("social", "1");
  if (nextPath && nextPath !== "/hub") {
    url.searchParams.set("next", nextPath);
  }
  return url.toString();
}

function socialProviderAllowed(
  providerId: string,
  options: {
    socialLoginEnabled: boolean;
    googleEnabled: boolean;
    appleEnabled: boolean;
  },
): boolean {
  if (!options.socialLoginEnabled) return false;
  const id = providerId.trim().toLowerCase();
  if (id === "google") return options.googleEnabled;
  if (id === "apple") return options.appleEnabled;
  return false;
}

function SocialProviderMark({ providerId }: { providerId: string }) {
  const id = providerId.trim().toLowerCase();
  const label = id === "google" ? "G" : id === "apple" ? "ID" : "SSO";
  return (
    <span
      aria-hidden="true"
      className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded border border-rule bg-elevated px-1 font-display text-[0.62rem] font-semibold uppercase leading-none text-ink"
    >
      {label}
    </span>
  );
}

function isMfaLoginRequired(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  const detail = err.detail;
  if (!detail || typeof detail !== "object") return false;
  return (
    "code" in detail &&
    String((detail as { code: unknown }).code) === "mfa_login_required"
  );
}

function accountLinkRequestFromError(err: unknown): SocialAccountLinkRequest | null {
  if (!(err instanceof ApiError)) return null;
  const detail = err.detail;
  if (!detail || typeof detail !== "object") return null;
  const code = "code" in detail ? String((detail as { code: unknown }).code) : "";
  if (code !== "account_link_required") return null;
  const email = "email" in detail ? String((detail as { email: unknown }).email) : "";
  const provider =
    "provider" in detail ? String((detail as { provider: unknown }).provider) : "";
  const providerLabel =
    "provider_label" in detail
      ? String((detail as { provider_label: unknown }).provider_label)
      : providerDisplayName(provider);
  return {
    email,
    provider,
    providerLabel: providerLabel || providerDisplayName(provider),
  };
}

function providerDisplayName(provider: string): string {
  const id = provider.trim().toLowerCase();
  if (id === "google") return "Google";
  if (id === "apple") return "Apple ID";
  return "this social account";
}

function accountLinkErrorCopy(err: unknown): string {
  if (err instanceof ApiError) {
    const code =
      err.detail && typeof err.detail === "object" && "code" in err.detail
        ? String((err.detail as { code: unknown }).code)
        : "";
    if (code === "invalid_password") {
      return "That password did not match this Tktic account.";
    }
    if (code === "disabled_user") {
      return "This Tktic account is disabled. Contact your team admin for access.";
    }
    if (code === "social_login_disabled" || code === "social_provider_disabled") {
      return "That social sign-in option is currently disabled.";
    }
    if (code === "account_link_conflict") {
      return "This social account is already linked to another Tktic account.";
    }
    if (err.status === 429) {
      return "Too many password attempts. Try again in a minute.";
    }
    return "Could not link this social account. Please try again.";
  }
  return "Network error. Try again.";
}

function exchangeErrorCopy(err: unknown): string {
  if (err instanceof ApiError) {
    const code =
      err.detail && typeof err.detail === "object" && "code" in err.detail
        ? String((err.detail as { code: unknown }).code)
        : "";
    if (code === "verified_email_required") {
      return "Please verify your email before signing in.";
    }
    if (code === "disabled_user") {
      return "This Tktic account is disabled. Contact your team admin for access.";
    }
    if (code === "social_login_disabled" || code === "social_provider_disabled") {
      return "That social sign-in option is currently disabled.";
    }
    if (code === "registration_disabled") {
      return "Account registration is currently closed. Contact your club admin for access.";
    }
    if (code === "mfa_login_required") {
      // Normally intercepted before this copy renders (the inline step-up
      // takes over) — defensive fallback only.
      return "Enter your second factor to finish signing in.";
    }
    if (err.status === 429) {
      return "Too many sign-in attempts. Try again in a minute.";
    }
    return "Sign-in could not finish. Please try again.";
  }
  return "Network error. Try again.";
}
